
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useMemo } from "react";
import { formatDateDDMMYYYY, formatDateDDMMYYYYHHMMSS_UTC, formatDateFeeDetailsDDMMYYYYHHMMSS } from "./data&dateTime";
import Notifier from "./notify";




const formatINR = (val) => {
  const num = cleanNumber(val);
  return `Rs. ${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};



const cleanNumber = (val) => {
  if (!val) return 0;
  return parseFloat(String(val).replace(/[^\d.-]/g, "")) || 0;
};

export const downloadLedgerPDF = (selectedFeeDetails) => {
  if (!selectedFeeDetails) return;

  const doc = new jsPDF();
  const startY = 20;

  doc.setFontSize(14);
  doc.text(`${selectedFeeDetails?.name?.toUpperCase()}'s Payment Ledger`, 14, 15);

  // ❌ Course column removed
  const tableColumn = ["Date", "Particular", "Debit", "Credit", "Balance"];
  const tableRows = [];

  let runningBalance = 0;

  selectedFeeDetails?.ledger_data?.forEach((txn) => {
    const isCredit = txn.payment_type === "Credit";
    const amount = parseFloat(txn.amount) || 0;

    const debit = isCredit ? 0 : amount;
    const credit = isCredit ? amount : 0;

    runningBalance += credit - debit;

    // Correct Particular Logic
    const particular = isCredit
      ? (txn.mode_of_payment || txn.transaction?.mode_of_payment || "N/A") // Credit → show mode
      // Debit → fee head, falling back to the mode. Refund debits carry no
      // fee head, so without the fallback they render as a bare "N/A".
      : (txn.fee_head || txn.mode_of_payment || "N/A");

    tableRows.push([
      formatDateDDMMYYYYHHMMSS_UTC(txn.created_at),
      particular,
      formatINR(debit),
      formatINR(credit),
      formatINR(runningBalance),
    ]);
  });

  // Net Balance row
  tableRows.push([
    "Net Balance (Credit - Debit)",
    "",
    "",
    "",
    formatINR(runningBalance),
  ]);

  // PDF Table
  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY,
    styles: { fontSize: 10, cellPadding: 3 },
    headStyles: { fillColor: [127, 86, 218] },
    columnStyles: {
      0: { cellWidth: 30, halign: "center" }, // Date
      1: { cellWidth: 60, halign: "left" },   // Particular
      2: { cellWidth: 30, halign: "right" },  // Debit
      3: { cellWidth: 30, halign: "right" },  // Credit
      4: { cellWidth: 30, halign: "right" },  // Balance
    },
    tableWidth: "wrap",
    didParseCell: (data) => {
      if (data.section === "head" && [2, 3, 4].includes(data.column.index)) {
        data.cell.styles.halign = "right";
      }
    },
  });

  doc.save(`${selectedFeeDetails?.name || "student"}_ledger.pdf`);
};


/** ---------- PDF Download Function ---------- **/


export const downloadScheduledPDF = (selectedFeeDetails, scheduleData) => {
  if (!scheduleData || scheduleData.length === 0) return;

  const doc = new jsPDF();
  doc.setFont("times", "normal"); // <-- ensure ₹ renders
  doc.setFontSize(14);
  doc.text(`${selectedFeeDetails?.name?.toUpperCase()}'s Scheduled Fee`, 14, 16);

  const tableColumn = ["Fee Head", "Course", "Due Date", "Scheduled Amount"];
  const tableRows = [];

  let totalAmount = 0;

  scheduleData.forEach(txn => {
    const amount = parseFloat(txn.amount) || 0;
    totalAmount += amount;

    tableRows.push([
      txn.fee_head || "N/A",
      selectedFeeDetails.course_name || "-",
      txn.due_date || "-",
      formatINR(amount)
    ]);
  });

  // Add total row
  tableRows.push([
    "Total",
    "",
    "",
    formatINR(totalAmount)
  ]);

  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 25,
    styles: { fontSize: 10, halign: 'left' }, // default for body
    headStyles: { fillColor: [127, 86, 218] }, // remove halign here
    columnStyles: {
      3: { halign: 'right' }, // body alignment
    },
    didParseCell: (data) => {
      // Right-align the header of the 4th column (Scheduled Amount)
      if (data.section === 'head' && data.column.index === 3) {
        data.cell.styles.halign = 'right';
      }
    },
    tableWidth: 'auto',
  });

  doc.save(`${selectedFeeDetails?.name || "student"}_scheduled_fee.pdf`);
};

export const downloadPlanPDF = (selectedFeeDetails) => {
  if (!selectedFeeDetails) return;

  const doc = new jsPDF();
  const startY = 20;

  // Header
  doc.setFontSize(14);
  doc.text(`${selectedFeeDetails?.name?.toUpperCase()}'s Imposed Fee`, 14, 15);

  // Table columns
  const tableColumn = ["Course", "Fee Head", "Amount"];
  const tableRows = [];

  const feePlans = selectedFeeDetails?.payment_fee_plan || [];
  feePlans.forEach((plan) => {
    tableRows.push([
      selectedFeeDetails.course_name || "N/A",
      plan.fee_type_head || "N/A",
      formatINR(plan.amount),
    ]);
  });

  // Footer total
  const totalAmount = feePlans.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  tableRows.push([
    "Total",
    "",
    formatINR(totalAmount),
  ]);



  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY,
    styles: { fontSize: 10, cellPadding: 3 }, // default body
    headStyles: { fillColor: [127, 86, 218] }, // remove halign here
    columnStyles: {
      0: { cellWidth: 55, halign: 'left' }, // Semester / Academic Group
      1: { cellWidth: 55, halign: 'left' }, // Fee Head
      2: { cellWidth: 50, halign: 'right' }, // Amount
    },
    tableWidth: 'auto',
    didParseCell: (data) => {
      // Right-align header cell for Amount column
      if (data.section === 'head' && data.column.index === 2) {
        data.cell.styles.halign = 'right';
      }
    },
  });



  // Save PDF
  doc.save(`${selectedFeeDetails?.name || "student"}_imposed_fee.pdf`);
};





/////////////////////////Print methods


/** ---------- Print Ledger ---------- **/
export const printLedger = (selectedFeeDetails) => {
  if (!selectedFeeDetails) return;

  let runningBalance = 0;

  const tableRows = selectedFeeDetails.ledger_data.map(txn => {
    const isCredit = txn.payment_type === "Credit";
    const amount = parseFloat(txn.amount) || 0;

    // NEW LOGIC → If fee head missing → show mode_of_payment. Applies to
    // credits and to refund debits, neither of which carries a fee head.
    const particular =
      (!txn.fee_head || txn.fee_head === "null" || txn.fee_head === null)
        ? txn.mode_of_payment || (isCredit ? "Cash" : "N/A")
        : txn.fee_head;

    // update running balance
    if (isCredit) {
      runningBalance += amount;
    } else {
      runningBalance -= amount;
    }

    return `
      <tr>
        <td>${formatDateDDMMYYYYHHMMSS_UTC(txn.created_at)}</td>
        <td>${particular}</td>
        <td style="text-align:right">${formatINR(!isCredit ? amount : 0)}</td>
        <td style="text-align:right">${formatINR(isCredit ? amount : 0)}</td>
        <td style="text-align:right">${formatINR(runningBalance)}</td>
      </tr>
    `;
  }).join("");

  const totalDebit = selectedFeeDetails.ledger_data.reduce(
    (sum, txn) => txn.payment_type !== "Credit" ? sum + parseFloat(txn.amount || 0) : sum,
    0
  );

  const totalCredit = selectedFeeDetails.ledger_data.reduce(
    (sum, txn) => txn.payment_type === "Credit" ? sum + parseFloat(txn.amount || 0) : sum,
    0
  );

  const footerRows = `
    <tr style="font-weight:bold">
      <td>Total</td><td></td>
      <td style="text-align:right">${formatINR(totalDebit)}</td>
      <td style="text-align:right">${formatINR(totalCredit)}</td>
      <td style="text-align:right">${formatINR(totalCredit - totalDebit)}</td>
    </tr>
  `;

  const html = `
    <html>
      <head>
        <title>${selectedFeeDetails.name} Ledger</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { border: 1px solid #222; padding: 6px; word-wrap: break-word; }
          th { background: #7F56DA; color: #fff; }
          th:nth-child(1) { width: 15%; }
          th:nth-child(2) { width: 18%; }
          th:nth-child(3) { width: 18%; }
          th:nth-child(4), th:nth-child(5), th:nth-child(6) { width: 16%; }
        </style>
      </head>
      <body>
      <style>
    td.right-align, th.right-align {
      text-align: right;
    }
  </style>
        <h2>${selectedFeeDetails.name.toUpperCase()}'s Payment Ledger</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Particular</th>
              <th class="right-align">Debit</th>
        <th class="right-align">Credit</th>
        <th class="right-align">Balance</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
          <tfoot>${footerRows}</tfoot>
        </table>
      </body>
    </html>
  `;

  const newWindow = window.open("", "_blank");
  newWindow.document.write(html);
  newWindow.document.close();
  newWindow.focus();
  newWindow.print();
  newWindow.close();
};

/** ---------- Print Scheduled Fee ---------- **/
export const printScheduledFee = (selectedFeeDetails, scheduleData) => {
  if (!scheduleData || scheduleData.length === 0) return;

  let totalAmount = 0;

  // Build table rows including total row
  const tableRows = [
    ...scheduleData.map(txn => {


      const amount = parseFloat(txn.amount) || 0;
      totalAmount += amount;
      return `
        <tr>
          <td>${txn.fee_head || "N/A"}</td>
          <td>${selectedFeeDetails?.course_name || "N/A"}</td>
          <td>${txn.due_date || "-"}</td>
          <td style="text-align:right">${formatINR(amount)}</td>
        </tr>
      `;
    }),
    `
      <tr style="font-weight:bold">
        <td>Total</td><td></td><td></td>
        <td style="text-align:right">${formatINR(totalAmount)}</td>
      </tr>
    `
  ].join("");

  const html = `
    <html>
      <head>
        <title>${selectedFeeDetails.name} Scheduled Fee</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #222; padding: 6px; }
          th { background: #7F56DA; color: #fff; }
        </style>
      </head>
      <body>
        <h2>${selectedFeeDetails.name.toUpperCase()}'s Scheduled Fee</h2>
        <table>
          <thead>
            <tr>
              <th>Fee Head</th>
              <th>Course</th>
              <th>Due Date</th>
              <th>Scheduled Amount</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const newWindow = window.open("", "_blank");
  newWindow.document.write(html);
  newWindow.document.close();
  newWindow.focus();
  newWindow.print();
  newWindow.close();
};


/** ---------- Print Imposed Fee / Fee Plan ---------- **/
export const printPlan = (selectedFeeDetails) => {
  if (!selectedFeeDetails) return;

  const feePlans = selectedFeeDetails.payment_fee_plan || [];
  const totalAmount = feePlans.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const tableRows = feePlans.map(plan => `
    <tr>
      <td>${selectedFeeDetails?.course_name || "N/A"}</td>
      <td>${plan.fee_type_head || "N/A"}</td>
      <td style="text-align:right">${formatINR(plan.amount)}</td>
    </tr>
  `).join("");

  const html = `
    <html>
      <head>
        <title>${selectedFeeDetails.name} Imposed Fee</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #222; padding: 6px; }
          th { background: #7F56DA; color: #fff; }
        </style>
      </head>
      <body>
        <h2>${selectedFeeDetails.name.toUpperCase()}'s Imposed Fee</h2>
        <table>
          <thead>
            <tr>
              <th>Course</th><th>Fee Head</th><th>Amount</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
          <tfoot>
            <tr style="font-weight:bold">
              <td>Total</td><td></td><td style="text-align:right">${formatINR(totalAmount)}</td>
            </tr>
          </tfoot>
        </table>
      </body>
    </html>
  `;

  const newWindow = window.open("", "_blank");
  newWindow.document.write(html);
  newWindow.document.close();
  newWindow.focus();
  newWindow.print();
  newWindow.close();
};


/** ---------- Share Fee Details as PDF ---------- **/
export const shareFeeDetailsPDF = async (viewMode, selectedFeeDetails) => {
  if (!selectedFeeDetails) return;

  try {
    const doc = new jsPDF();
    doc.setFontSize(14);

    let tableColumns = [];
    let tableRows = [];

    // Header
    if (viewMode === "ledger") {
      doc.text(`${selectedFeeDetails.name.toUpperCase()}'s Payment Ledger`, 14, 15);
      tableColumns = ["Date", "Particular", "Academic Group", "Debit", "Credit"];
      selectedFeeDetails.ledger_data.forEach(txn => {
        const isCredit = txn.payment_type === "Credit";
        tableRows.push([
          isCredit ? txn.payment_date : txn.due_date,
          // Credits and refund debits have no fee head — fall back to the mode.
          txn.fee_head || txn.mode_of_payment || "N/A",
          selectedFeeDetails?.course_name || "N/A",
          !isCredit ? formatINR(txn.amount) : formatINR(0),
          isCredit ? formatINR(txn.amount) : formatINR(0)
        ]);
      });
    } else if (viewMode === "scheduled") {
      doc.text(`${selectedFeeDetails.name.toUpperCase()}'s Scheduled Fee`, 14, 15);
      tableColumns = ["Fee Head", "Course", "Due Date", "Amount"];
      (selectedFeeDetails.payment_schedule || []).forEach(txn => {
        tableRows.push([
          txn.fee_head || "N/A",
          selectedFeeDetails?.course_name || "-",
          txn.due_date || "-",
          formatINR(txn.amount)
        ]);
      });
    } else if (viewMode === "plan") {
      doc.text(`${selectedFeeDetails.name.toUpperCase()}'s Imposed Fee`, 14, 15);
      tableColumns = ["Course", "Fee Head", "Amount"];
      (selectedFeeDetails.payment_fee_plan || []).forEach(plan => {
        tableRows.push([
          selectedFeeDetails?.course_name || "N/A",
          plan.fee_type_head || "N/A",
          formatINR(plan.amount)
        ]);
      });
    }

    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 25,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [127, 86, 218] },
      columnStyles: {
        [tableColumns.length - 1]: { halign: "right" } // last column right-aligned
      }
    });


    const pdfBlob = doc.output("blob");
    const file = new File([pdfBlob], `${selectedFeeDetails.name || "student"}_${viewMode}.pdf`, { type: "application/pdf" });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: `${selectedFeeDetails.name} - Fee Details`,
        text: `Sharing ${viewMode} details for ${selectedFeeDetails.name}.`
      });
    } else {
      Notifier.error("Sharing not supported on this device. PDF will be downloaded instead.");
      doc.save(`${selectedFeeDetails.name || "student"}_${viewMode}.pdf`);
    }

  } catch (error) {
    console.error("Failed to share fee details PDF:", error);
    Notifier.error("Error sharing fee details.");
  }
};
